<template>
    <div>
        <div v-if="videos != null">
            <div id="convert-result" class="resultContainer">
                <div class="contentWrapper">
                    <div class="resultThumb resultInfoContainer">
                        <img class="thumbImage" :src="thumb" :alt="meta.title">
                        <h6 class="ytVideoTitle">{{ stringShorter(meta.title, 20) }}</h6>
                        <p>{{ meta.duration }}</p>
                    </div>
                    <div class="resultContent">
                        <div class="tableWrapper">
                            <div class="tabContent">
                                <div id="nav-mp4" class="tabPane active show">
                                    <table class="resultTable">
                                        <thead>
                                            <tr>
                                                <th>File type</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(video) in videosList" :key="video.url">
                                                <td class="fileTypeCell" :class="{ 'noAudio': video.no_audio, 'audio': !video.no_audio }">
                                                    <span><strong class="quality">{{ video.quality }}</strong>.{{ video.ext }}</span>
                                                    <span class="noAudioIcon" v-if="video.no_audio"><i class="fas fa-volume-mute"></i></span>
                                                </td>
                                                <td class="actionCell" :class="{ 'noAudio': video.no_audio }">
                                                    <a class="downloadBtn"
                                                       :class="{ 'is-busy': isDownloading }"
                                                       :id="'download-' + video.ext + '-' + video.quality + '-' + (video.no_audio ? 'no-audio' : 'audio')"
                                                       @click="download(video.url, meta.title + '.' + video.ext, video)">
                                                        <i class="fas fa-download"></i> Download
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div @click="showMore = !showMore" class="showMoreButton"
                                         v-if="videos.length > listDefaultLength">
                                        <span>{{ showMore ? "Show less" : "Show more" }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="videos === null && manyElements !== null">
            <results-grid :items="manyElements">
                <template #default="{ items }">
                    <playlist-card
                        v-for="(manyElement, index) in items"
                        :key="manyElement.id || (manyElement.meta || {}).source || `idx-${index}`"
                        :preview="manyElement.thumb"
                        :title="(manyElement.meta || {}).title"
                        :source="(manyElement.meta || {}).source"
                        :initial-formats="manyElement.url"
                        :analytics-source="analyticsSource">
                    </playlist-card>
                </template>
            </results-grid>
        </div>
        <div v-if="error">
            <ErrorBlock :error="error" />
        </div>
        <loader-component v-if="isLoading" :is-loading="isLoading"></loader-component>
    </div>
</template>

<script>
import ErrorBlock from "./ErrorBlock";
import PlaylistCard from "./PlaylistCard";
import ResultsGrid from "./ResultsGrid";
import LoaderComponent from "./LoaderComponent";
import stringShorter from '../tools/stringShorter'
import {REQUEST_POOL, isAbortError, requestConvert} from "../api/convert";
import {NO_FORMATS_REASON, buildConvertErrorResult, buildNoFormatsResult} from "../tools/downloadContracts";
import {ANALYTICS_SOURCE} from "../tools/ga";
import {dispatchConvertResponse, filterFormatsForTab, normalizeFormats} from "../tools/formats";
import downloadAttempts from "../mixins/downloadAttempts";
import {
    DIRECT_DOWNLOAD_DELAY,
    startDownloadWithReport,
} from "../tools/downloadActions";
import { expBus } from '@js-packages/experiment-loader';

// Эксперимент tzPlatformLinks: качества ролика выше лучшего MP4 со звуком из выдачи.
// Литерал save-from (Result.jsx) с R6; ссылки в ответе /api/convert — поле url, а не urls.
function getBetterQualities(data) {
  if (!data || !Array.isArray(data.video_quality) || !Array.isArray(data.url)) {
    return false;
  }
  const hostingYt = data.hosting === '101';

  if (!hostingYt) return false;

  const ytQuality = data.video_quality.map((q) => parseInt(q, 10)).filter((q) => q > 0);
  const videoQualities = data.url
    .filter(i => i.name === 'MP4' && (i.no_audio === false || i.no_audio === undefined))
    .map(i => i.qualityNumber || parseInt(i.quality) || 0)
    .filter(q => q >= 480);

  const maxVideoQuality = Math.max(...videoQualities, 480);
  const ytMaxQuality = Math.max(...ytQuality, 480);

  if (ytMaxQuality === maxVideoQuality) {
    return false;
  }

  const better = ytQuality.filter((q) => q > maxVideoQuality);
  better.duration = data.meta && data.meta.duration;
  return better;
}

export default {
    mixins: [downloadAttempts],
    components: {
        ErrorBlock,
        LoaderComponent,
        PlaylistCard,
        ResultsGrid,
    },
    name: 'MainResults',
    props: {
        activBtn: String,
        searchUrlInsPosts: String,
        searchUrlInsReels: String,
        highlightStories: Array,
        stories: Array,
        canonical: String
    },
    data() {
        return {
            videos: null,
            meta: null,
            link: null,
            thumb: null,
            audios: [],
            error: null,
            showMore: false,
            listDefaultLength: 3,
            analyticsSource: ANALYTICS_SOURCE.PLAYLIST,
            manyElements: null,
            linkStories: null,
            linkPosts: null,
            rootOnSearchResults: false,
            label: '',
            requestStartDate: 0,
            isLoading: false
        }
    },
    mounted() {
        // обработчики именованные: $root переживает компонент, а компонент
        // пересоздаётся на каждой смене вкладки — без $off подписки копятся,
        // и один 'link-submit' запускает request() во всех прошлых экземплярах
        this.onClearResults = () => {
            this.resetResults();
            // только очистка экрана сбрасывает и саму ссылку: новый запрос её,
            // наоборот, только что установил
            this.link = null;
            this.rootOnSearchResults = false;
        };
        this.$root.$on('clear-results', this.onClearResults);

        if (this.highlightStories) {
            this.videos = null
            this.manyElements = this.highlightStories
        }
        this.onLinkSubmit = (link) => {
            document.getElementById('main-form').scrollIntoView();
            this.link = link.trim();
            this.request(link);
        };
        this.$root.$on('link-submit', this.onLinkSubmit);
        if (this.activBtn) {
            document.getElementById('main-form').scrollIntoView();

            if (this.activBtn === 'posts' && this.searchUrlInsPosts) {
                this.$root.$emit('link-submit-posts', this.searchUrlInsPosts)
                this.linkPosts = this.searchUrlInsPosts;
                this.manyElements = null
                this.rootOnSearchResults = true;
                if (this.linkPosts && this.linkStories !== this.linkPosts) {
                    this.request(this.linkPosts);
                }
            }
            if (this.activBtn === 'stories') {
                this.manyElements = null;
                this.rootOnSearchResults = true;
                if (this.stories) {
                    this.videos = null
                    this.manyElements = this.stories
                }
                if (!this.manyElements) {
                    this.$emit('loadStories')
                } else {
                    if (this.stories === null) {
                        this.error = { message: 'Something went wrokng. Try later' }
                    } else if (!this.stories.length) {
                        this.error = { message: 'It seems that there are no stories for the last 24 hours' }
                    }
                }
            }
            if (this.activBtn === 'reels' && this.searchUrlInsReels) {
                this.$root.$emit('link-submit-reels', this.searchUrlInsReels)
                this.linkReels = this.searchUrlInsReels;
                this.manyElements = null;
                this.rootOnSearchResults = true;
                if (this.linkReels && this.linkReels !== this.linkPosts) {
                    this.request(this.linkReels);
                };
            }
        }

    },
    beforeDestroy() {
        // запрос отменяет хук миксина downloadAttempts: инстанс пересоздаётся на
        // каждой смене вкладки, а $root живёт дальше, и ответ по покинутой вкладке
        // иначе досылает 'show-results'/'show-error' и до 45с держит слот в пуле.
        //
        // подписки на $root сам Vue не снимает: $root живёт дальше компонента
        this.$root.$off('clear-results', this.onClearResults);
        this.$root.$off('link-submit', this.onLinkSubmit);
    },
    watch: {
        stories: function (newVal) {
            if (!newVal.length) {
                this.error = { message: 'It seems that there are no stories for the last 24 hours' }
            } else {
                this.error = null
                this.manyElements = newVal
            }
        },
    },
    computed: {
        // Метка источника для событий уровня запроса.
        //
        // Один и тот же компонент живёт в двух ролях: на главной (blade, без activBtn)
        // это обычная одиночная форма, и её события должны остаться 'direct'; внутри
        // SearchResults он всегда получает activ-btn и показывает сетку карточек,
        // помеченную 'playlist'. Безусловный analyticsSource пометил бы 'playlist'
        // и главную форму.
        //
        // Остаётся край: если Instagram-вкладка вернула ОДНО видео, оно рисуется
        // таблицей, а не сеткой, и его успех уходит без метки ('direct'), тогда как
        // отказ того же запроса получит 'playlist'. Развести это на стороне ошибки
        // нельзя — форма ответа тогда ещё неизвестна; см. оговорку про 'direct'
        // в README.
        funnelSource() {
            return this.activBtn ? this.analyticsSource : undefined;
        },
        videosList() {
            return this.showMore ? this.videos : this.videos.slice(0, this.listDefaultLength)
        },
    },
    methods: {
        // Источник обязателен: успех сетки уходит с меткой 'playlist', и без него
        // отказ того же запроса писался бы как 'direct' — сравнение success rate
        // по источникам, ради которого вводился dimension2, разъезжалось бы.
        // Единственное место, где перечислены поля выдачи: набор жил в двух копиях
        // (здесь и в обработчике 'clear-results'), и они уже разошлись.
        //
        // Чистим обе выдачи, а не только текущую: главный экземпляр монтируется один
        // раз (app.blade.php, без v-if/key) и переиспользуется на каждый submit, а
        // блоки в шаблоне стоят на независимых v-if — видео прошлого ответа скрывало
        // бы сетку нового (условие `videos === null && manyElements !== null`), а
        // ErrorBlock показывался бы поверх ещё живой таблицы.
        resetResults() {
            this.videos = null;
            this.manyElements = null;
            this.meta = null;
            this.thumb = null;
            this.audios = [];
            this.error = null;
            this.showMore = false;
            // иначе незавершённое скачивание прошлой ссылки держало бы кнопку
            // заблокированной на новой выдаче
            this.resetDownloadState();
        },
        handleError(result) {
            this.$root.$emit('show-error', result, this.link, this.funnelSource);
            this.$root.$emit('send-error-time', this.requestStartDate, this.link);
        },
        stringShorter(string, length) {
            return stringShorter(string, length)
        },
        startLoading() {
            this.isLoading = true
        },
        endLoading() {
            this.isLoading = false
        },
        request(link) {
            this.requestStartDate = Date.now();
            this.resetResults();

            // медленный ответ по прошлой ссылке иначе перезапишет уже показанную выдачу
            const controller = this.beginRequest();

            this.startLoading();

            requestConvert({
                link: link.replace('?source=search', ''),
                subscribeSignedRequestBody: this.$root.subscribeSignedRequestBody,
                signal: controller.signal,
                // PRIMARY — это два слота под двойной клик ОДНОЙ главной формы.
                // Экземпляр внутри SearchResults (у него задан activBtn) — уже вторая
                // форма на странице: деля те же два слота с постоянно смонтированной
                // главной, он мог простоять в очереди до QUEUE_WAIT_TIMEOUT и словить
                // queue_timeout на ровном месте. Его выдача — сетка, ей и пул сетки.
                pool: this.activBtn ? REQUEST_POOL.GRID : REQUEST_POOL.PRIMARY,
            })
                // Каждый колбэк сверяется с controller. Одного isAbortError мало:
                // abort() отклоняет промис микротаской, и продолжение устаревшей
                // цепочки успевает перезаписать videos/manyElements/meta данными
                // прошлой ссылки — молча, без индикации ошибки.
                .then((data) => {
                    if (this.isCurrentRequest(controller)) {
                        this.applyResponse(data);
                    }
                })
                .catch((e) => {
                    if (this.isCurrentRequest(controller)) {
                        this.applyRequestError(e);
                    }
                })
                .finally(() => {
                    if (this.isCurrentRequest(controller)) {
                        this.$root.$emit('result-end');
                        this.endLoading();
                    }
                });
        },
        applyResponse(data) {
            // !data, а не data === null: тело может прийти и undefined, и тогда
            // обращение к data.blacklist ниже бросило бы TypeError, неотличимый
            // в аналитике от любого другого клиентского бага
            if (!data) {
                this.error = { message: 'Failed to parse server response' };
                return;
            }

            // Business errors are returned with HTTP 200 and no `url` field. They are
            // valid API responses, not malformed format payloads; preserve the message
            // supplied by the backend (for example, "The download link not found.").
            if (data.success === false) {
                this.error = { message: data.message || 'The download link was not found' };
                return this.handleError(data);
            }

            if (data.blacklist) {
                console.warn('[main-results] content is blacklisted');
                this.error = { message: 'Content is blacklisted' };
                return this.handleError(data);
            }

            // перебор форм — общий с карточкой, см. tools/formats.js: пока ветки
            // стояли подряд здесь, они перекрывали друг друга и часть ответа
            // отбрасывалась молча
            return dispatchConvertResponse(data, {
                onGrid: (items) => this.applyGridResponse(items),
                onSingle: (item) => this.applySingleResponse(item),
                onEmptyList: () => {
                    if (!this.activBtn) {
                        return this.failWithMalformed(data);
                    }
                    this.label = 'It seems that there are no stories for the last 24 hours';
                    this.error = { message: this.label };
                    // result, а не строка: ga.js#pickErrorEvent читает поля объекта,
                    // и на примитиве все проверки дали бы undefined, уводя событие
                    // в обезличенный 'unclassified' вместо точной причины
                    return this.handleError(buildNoFormatsResult(data, NO_FORMATS_REASON.EMPTY));
                },
                onMalformed: () => this.failWithMalformed(data),
            });
        },
        // MALFORMED, а не EMPTY: «сервер прислал форму, которую мы не читаем» и
        // «предложить нечего» — разные сбои, и под одной меткой сломанный бэкенд был бы
        // неотличим от штатно пустой выдачи
        failWithMalformed(data) {
            this.error = { message: 'Invalid response format' };
            this.handleError(buildNoFormatsResult(data, NO_FORMATS_REASON.MALFORMED));
        },
        applyGridResponse(items) {
            // funnelSource, а не analyticsSource: метка должна совпасть с той, что
            // уйдёт при отказе ЭТОГО ЖЕ запроса (handleError). Жёсткий 'playlist'
            // давал обратную асимметрию — успех сетки с главной формы считался
            // 'playlist', а её отказ 'direct'. Карточки внутри по-прежнему шлют свои
            // события как 'playlist': это метка карточки, а не запроса.
            const filtered = filterFormatsForTab(items, this.activBtn);

            // Фильтр вкладки может не оставить ничего (в reels/stories держим только
            // mp4), а классификатор назвал ответ сеткой ещё до фильтрации. Условие
            // рендера — `manyElements !== null`, и пустой массив ему удовлетворяет:
            // без этой ветки пользователь получил бы пустую сетку молча.
            if (!filtered.length) {
                this.error = { message: 'No downloadable formats found' };
                return this.handleError(buildNoFormatsResult(items, NO_FORMATS_REASON.EMPTY));
            }

            this.$root.$emit('show-results', items[0], this.link, 'mp4', this.funnelSource);
            this.manyElements = filtered;
        },
        applySingleResponse(item) {
            // фильтр, дедуп и сортировка общие с карточкой выдачи, см. tools/formats.js:
            // раньше здесь была параллельная сборка, и списки качеств уже разошлись
            const formats = normalizeFormats(item.url);

            // Скачивать нечего: normalizeFormats отбрасывает аудиодорожки, и ответ,
            // где все url помечены audio, даёт пустой список. Условие рендера таблицы —
            // `videos != null`, и пустой массив ему удовлетворяет: без этой ветки
            // пользователь получил бы пустую таблицу, а событие ушло бы как успех.
            // Ср. ту же проверку в applyGridResponse() и в PlaylistCard.
            if (!formats.length) {
                this.error = { message: 'No downloadable formats found' };
                return this.handleError(buildNoFormatsResult(item, NO_FORMATS_REASON.EMPTY));
            }

            this.$root.$emit('show-results', item, this.link, 'mp4');

            this.videos = formats.map((format) => ({
                ...format.raw,
                // подпись качества нормализована так же, как в карточке ('1080p' -> '1080')
                quality: format.quality,
            }));

            this.audios = item.url.filter((format) => format.audio === true);

            this.meta = item.meta;
            this.thumb = item.thumb;

            window.videos = this.videos;

            // один эмит на выдачу и только для YouTube (hosting '101'): модуль ищет
            // таблицу результата при получении события, поэтому — после рендера
            const betterQuality = getBetterQualities(item);
            if (betterQuality) {
                this.$nextTick(() => expBus.emit('tzBestQuality', betterQuality));
            }
        },
        applyRequestError(e) {
            // запрос отменён более новым — молча выходим, выдачу рисует он
            if (isAbortError(e)) {
                return;
            }

            // .catch() стоит после .then(), поэтому сюда долетает и баг разбора
            // успешного ответа — см. logUnexpectedFailure в миксине
            this.logUnexpectedFailure(e, 'main-results');

            // объект ошибки строит tools/downloadContracts.js — иначе бизнес-поля тела
            // остались бы вложенными в data, и ga.js#showError не смог бы отличить
            // 'not_supported' от общего 'link_not_found'
            const result = buildConvertErrorResult(e);

            if (e.response?.status >= 400) {
                if (e.response.status === 401) {
                    this.label = e.response.data?.error_message;
                    this.handleError(result);
                    this.error = true;
                    return;
                }

                this.label = 'The download link was not found';
                this.handleError(result);
                this.error = { message: 'The download link was not found' };
                return;
            }

            this.handleError(result);

            // Без this.error экран остаётся пустым: videos/manyElements = null,
            // лоадер снят, ErrorBlock не отрисован. Сюда попадают сетевые сбои
            // и отказ очереди запросов (QUEUE_WAIT_TIMEOUT) — у них нет e.response,
            // поэтому ветка выше их не покрывает. Ср. PlaylistCard.fail().
            this.error = {
                message: result?.queue_timeout
                    ? 'The service is busy right now. Please try again'
                    : 'Something went wrong. Please try again',
            };
        },
        sendToForm(url) {
            localStorage.setItem("search", url);
            document.location = this.canonical;
        },
        download(link, filename, videoObj) {
            // <a> нельзя пометить disabled — повторный клик отсекаем здесь, иначе
            // несколько быстрых кликов запустят параллельные конвертации
            if (this.isDownloading) {
                return;
            }
            // формат берём из самого видео: в карточке выдачи в dimension1 уходит
            // реальное расширение, и захардкоженный 'mp4' разошёлся бы с ней
            const ext = videoObj?.ext || 'mp4';
            // this.link переприсваивается на каждый submit — поздний отказ конвертера
            // должен отчитаться за ту ссылку, которую качали
            const attemptLink = this.link;
            // ссылка может и не смениться (тот же ролик, другое качество) — тогда
            // устаревшую попытку отличает только номер
            const { attempt, finish } = this.beginDownload();

            // обвязка «скачать и отчитаться» общая с карточкой выдачи,
            // см. startDownloadWithReport в tools/downloadActions.js
            startDownloadWithReport({
                url: link,
                videoObj,
                filename,
                isAudio: ext === 'mp3',
                thumb: this.thumb,
                delay: DIRECT_DOWNLOAD_DELAY,
                // событие шлём только для прямой ссылки — как и раньше
                // источник обязателен: отказ того же скачивания уходит с funnelSource,
                // и без него успех считался бы 'direct', а провал 'playlist'
                beforeDirectDownload: () => this.$root.$emit('download-click', this.link, ext, this.funnelSource),
                // отказ скачивания тоже должен попадать в воронку, иначе success
                // rate по 'direct' окажется завышенным. Общая часть — в миксине.
                onFailure: (reason, suffix) => this.reportDownloadFailure(reason, {
                    attempt,
                    suffix,
                    // пока шла конвертация, отправили другую ссылку
                    stale: attemptLink !== this.link,
                    apply: (message, result) => {
                        this.error = { message };
                        this.$root.$emit('show-error', result, attemptLink, this.funnelSource);
                    },
                }),
                onSettled: finish,
            });
        },
        convert() {
            return this.$root.$emit('convert-link', this.link);
        },
    },
}

</script>
